'use client'

import { useState, type FormEvent } from 'react'
import { flushSync } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { EnumSelect } from '@/components/ui/enum-select'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { FormDialog, FormDialogContent } from '@/components/ui/form-dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiClient } from '@/lib/api/client'
import type {
  Customer,
  CustomerPortalStatus,
  CustomerSignInEmailStatus,
  CustomerType,
} from '@/lib/api/types'
import { customerIdentityValue } from '@/lib/customer-identity'
import { TextAreaField, TextField, formatLabel } from '@/src/components/wizard/fields'
import { SignInEmailPanel } from '@/src/components/SignInEmailPanel'
import { AddressesEditor, ContactsEditor } from './CustomerListEditors'
import {
  addressDrafts,
  buildAddressesRequest,
  buildContactsRequest,
  contactDrafts,
  customerEditChanges,
  customerEditErrorsFromApi,
  listChanged,
  socialProfileValues,
  validateCustomerEdit,
  validateCustomerLists,
  type AddressDraft,
  type ContactDraft,
  type CustomerEditField,
  type CustomerEditFieldErrors,
  type CustomerEditFormValues,
} from './customer-edit-errors'

type Tab = 'details' | 'contacts' | 'addresses' | 'online'

const PORTAL_STATUSES: CustomerPortalStatus[] = ['DISABLED', 'INVITED', 'ACTIVE', 'SUSPENDED']
const SOCIAL_FIELDS = [
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
  { key: 'x', label: 'X', placeholder: '@company' },
  { key: 'instagram', label: 'Instagram', placeholder: '@company' },
] as const

/** In the order the form shows them, so the first error found is the first one on screen. */
const FIELD_IDS: Record<CustomerEditField, string> = {
  type: 'customer-edit-type',
  company_name: 'customer-edit-company-name',
  name: 'customer-edit-name',
  contact_person: 'customer-edit-contact-person',
  email: 'customer-edit-email',
  phone: 'customer-edit-phone',
  tin_number: 'customer-edit-tin',
  default_currency: 'customer-edit-currency',
  customer_language: 'customer-edit-language',
  whatsapp_group_id: 'customer-edit-whatsapp-group',
  notes: 'customer-edit-notes',
  portal_status: 'customer-edit-portal-status',
  website_url: 'customer-edit-website',
  social_profiles: 'customer-edit-social-linkedin',
}

const ONLINE_FIELDS = new Set<CustomerEditField>([
  'portal_status',
  'website_url',
  'social_profiles',
])

/** What the form starts from. Placeholder email, phone and TIN start empty, never as text to keep. */
function formValues(customer: Customer): CustomerEditFormValues {
  return {
    name: customer.name,
    type: customer.type,
    companyName: customer.company_name || '',
    contactPerson: customer.contact_person || '',
    tinNumber: customerIdentityValue(customer, 'tin_number'),
    phone: customerIdentityValue(customer, 'phone'),
    email: customerIdentityValue(customer, 'email'),
    whatsappGroupId: customer.whatsapp_group_id || '',
    defaultCurrency: customer.default_currency || 'USD',
    customerLanguage: customer.customer_language || 'English',
    websiteUrl: customer.website_url || '',
    notes: customer.notes || '',
    portalStatus: customer.portal_status || 'DISABLED',
    socialProfiles: socialProfileValues(customer.social_profiles),
  }
}

interface CustomerEditDialogProps {
  customer: Customer
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: (customer: Customer) => void
  /** The customer's confirmed login, whose email moves only once the new address confirms. */
  signIn?: CustomerSignInEmailStatus | null
  /** A sign-in email change was started, resent or cancelled from the dialog. */
  onSignInChange?: () => void
}

/**
 * Edits a customer's details in place. Mount it afresh for each opening (give
 * it a new key) so it starts from the customer as last loaded.
 *
 * Only changed fields are sent. The customer ID, warehouse code and Zoho link
 * are Helm's, so they are shown, not offered for editing. When the customer
 * signs in with their email, it is not edited here either: "Change sign-in
 * email" sends a link to the new address, and nothing moves until it is used.
 */
export function CustomerEditDialog({
  customer,
  open,
  onOpenChange,
  onSaved,
  signIn = null,
  onSignInChange,
}: CustomerEditDialogProps) {
  const [initial] = useState(() => formValues(customer))
  const [values, setValues] = useState(initial)
  const [initialContacts] = useState(() => contactDrafts(customer.contacts))
  const [contacts, setContacts] = useState<ContactDraft[]>(initialContacts)
  const [initialAddresses] = useState(() => addressDrafts(customer.addresses))
  const [addresses, setAddresses] = useState<AddressDraft[]>(initialAddresses)
  const [listErrors, setListErrors] = useState<Record<string, string>>({})
  const [tab, setTab] = useState<Tab>('details')
  const [fieldErrors, setFieldErrors] = useState<CustomerEditFieldErrors>({})
  const [portalConfirm, setPortalConfirm] = useState<{
    pending: {
      changes: Record<string, unknown>
      contactsRequest: ReturnType<typeof buildContactsRequest>
      addressesRequest: ReturnType<typeof buildAddressesRequest>
    }
    reason: string
    error: string | null
  } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Known when the page loaded, or learnt from the API refusing an edit.
  const [signsIn, setSignsIn] = useState(Boolean(signIn?.login))
  const [signInPanel, setSignInPanel] = useState({ key: 0, open: false, newEmail: '' })
  const isBusiness = values.type === 'BUSINESS'

  function clearError(field: CustomerEditField) {
    setFieldErrors((current) => {
      if (!current[field]) return current
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  function set<K extends keyof CustomerEditFormValues>(
    key: K,
    field: CustomerEditField,
    value: CustomerEditFormValues[K],
  ) {
    setValues((current) => ({ ...current, [key]: value }))
    clearError(field)
  }

  function clearListError(key: string) {
    setListErrors((current) => {
      if (!current[key]) return current
      const next = { ...current }
      delete next[key]
      return next
    })
  }

  /** Opens the tab holding the first problem, in the order the tabs show them, and focuses it. */
  function showFirstError(errors: CustomerEditFieldErrors, lists: Record<string, string>) {
    const fields = (Object.keys(FIELD_IDS) as CustomerEditField[]).filter((field) => errors[field])
    const detail = fields.find((field) => !ONLINE_FIELDS.has(field))
    const contact = Object.keys(lists).find((key) => key.startsWith('contact-'))
    const address = Object.keys(lists).find((key) => key.startsWith('address-'))
    const online = fields.find((field) => ONLINE_FIELDS.has(field))
    const [target, id]: [Tab, string] | [null, null] = detail
      ? ['details', FIELD_IDS[detail]]
      : contact
        ? ['contacts', contact]
        : address
          ? ['addresses', address]
          : online
            ? ['online', FIELD_IDS[online]]
            : [null, null]
    if (!target) return
    flushSync(() => setTab(target))
    document.getElementById(id)?.focus()
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)

    const errors = validateCustomerEdit(values, initial)
    const rowErrors = validateCustomerLists(contacts, addresses)
    setFieldErrors(errors)
    setListErrors(rowErrors)
    if (Object.keys(errors).length > 0 || Object.keys(rowErrors).length > 0) {
      showFirstError(errors, rowErrors)
      return
    }

    const changes: Record<string, unknown> = { ...customerEditChanges(values, initial) }
    const contactsRequest = buildContactsRequest(contacts)
    const addressesRequest = buildAddressesRequest(addresses)
    if (listChanged((rows) => buildContactsRequest(rows).contacts, contacts, initialContacts)) {
      changes.contacts = contactsRequest.contacts
      // Sent as they are, so a new main contact cannot quietly change who the customer is.
      if (!('name' in changes)) changes.name = customer.name
      if (!('contact_person' in changes)) changes.contact_person = customer.contact_person ?? null
      if (!('email' in changes)) changes.email = customer.email
      if (!('phone' in changes)) changes.phone = customer.phone
    }
    if (listChanged((rows) => buildAddressesRequest(rows).addresses, addresses, initialAddresses)) {
      changes.addresses = addressesRequest.addresses
    }
    if (Object.keys(changes).length === 0) {
      onOpenChange(false)
      return
    }
    // Suspending, or lifting it, is confirmed first, with the reason for the audit log.
    if (
      'portal_status' in changes &&
      (changes.portal_status === 'SUSPENDED' || initial.portalStatus === 'SUSPENDED')
    ) {
      setPortalConfirm({
        pending: { changes, contactsRequest, addressesRequest },
        reason: '',
        error: null,
      })
      return
    }
    await save(changes, contactsRequest, addressesRequest)
  }

  async function confirmPortalChange() {
    if (!portalConfirm) return
    const reason = portalConfirm.reason.trim()
    if (suspending && !reason) {
      setPortalConfirm({ ...portalConfirm, error: 'Say why, so the audit log explains it' })
      document.getElementById('customer-edit-portal-reason')?.focus()
      return
    }
    const { changes, contactsRequest, addressesRequest } = portalConfirm.pending
    setPortalConfirm(null)
    await save(
      { ...changes, portal_status_reason: reason || null },
      contactsRequest,
      addressesRequest,
    )
  }

  async function save(
    changes: Record<string, unknown>,
    contactsRequest: ReturnType<typeof buildContactsRequest>,
    addressesRequest: ReturnType<typeof buildAddressesRequest>,
  ) {
    setBusy(true)
    let updated: Customer
    try {
      updated = await apiClient.updateCustomer(customer.id, changes)
    } catch (error) {
      const mapped = customerEditErrorsFromApi(error)
      if (
        mapped.fields.email?.code === 'LOCKED' &&
        'email' in changes &&
        values.email !== initial.email
      ) {
        // They sign in with it: move it through the confirmation link instead,
        // starting from what was typed, and keep the other edits to save.
        const typed = values.email
        flushSync(() => {
          setBusy(false)
          setSignsIn(true)
          setValues((current) => ({ ...current, email: initial.email }))
          setSignInPanel((current) => ({ key: current.key + 1, open: true, newEmail: typed }))
          setFieldErrors({})
          setFormError(null)
          setTab('details')
        })
        document.getElementById('customer-edit-new-sign-in-email')?.focus()
        return
      }
      // The API numbers list rows as sent; the editors number them as shown.
      const rowErrors = Object.fromEntries(
        Object.entries(mapped.lists).map(([key, message]) => {
          const [kind, index, field] = key.split('-') as [string, string, string]
          const rows = kind === 'contact' ? contactsRequest.sourceRows : addressesRequest.sourceRows
          return [`${kind}-${rows[Number(index)] ?? index}-${field}`, message]
        }),
      )
      // Re-enable the fields first: a disabled input cannot take focus.
      flushSync(() => {
        setBusy(false)
        setFieldErrors(mapped.fields)
        setListErrors(rowErrors)
        setFormError(mapped.form)
      })
      showFirstError(mapped.fields, rowErrors)
      return
    }
    setBusy(false)
    onSaved(updated)
  }

  const error = (field: CustomerEditField) => fieldErrors[field]?.message
  const suspending = values.portalStatus === 'SUSPENDED' && initial.portalStatus !== 'SUSPENDED'
  const lifting = initial.portalStatus === 'SUSPENDED' && values.portalStatus !== 'SUSPENDED'
  const who = signIn?.login?.email ?? 'this customer'

  return (
    <FormDialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <FormDialogContent
        title="Edit customer"
        description="Correct the customer's details. Only the fields you change are saved."
        submitLabel="Save changes"
        busyLabel="Saving customer"
        busy={busy}
        error={formError}
        errorTitle="The customer was not saved"
        onSubmit={handleSubmit}
        size="lg"
      >
        <Tabs value={tab} onValueChange={(next) => setTab(next as Tab)} className="gap-4">
          <TabsList className="grid w-full grid-cols-4" aria-label="Customer details">
            <TabsTrigger value="details" className="px-1 text-xs sm:text-sm">
              Details
            </TabsTrigger>
            <TabsTrigger value="contacts" className="px-1 text-xs sm:text-sm">
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="addresses" className="px-1 text-xs sm:text-sm">
              Addresses ({addresses.length})
            </TabsTrigger>
            <TabsTrigger value="online" className="px-1 text-xs sm:text-sm">
              Online
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="flex flex-col gap-[var(--form-section-gap)]">
            {customer.is_placeholder && (
              <Alert>
                <AlertDescription>
                  Provisional identity. Replace the missing details with staff-confirmed ones;
                  nothing extracted from a message is copied in for you.
                </AlertDescription>
              </Alert>
            )}

            <FieldGroup>
              <Field>
                <FieldLabel htmlFor={FIELD_IDS.type}>Customer type</FieldLabel>
                <EnumSelect
                  value={values.type}
                  onValueChange={(value) => {
                    set('type', 'type', value as CustomerType)
                    clearError('company_name')
                  }}
                  options={[
                    { value: 'BUSINESS', label: 'Business' },
                    { value: 'INDIVIDUAL', label: 'Individual' },
                  ]}
                  triggerProps={{ id: FIELD_IDS.type, className: 'sm:max-w-xs' }}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                {isBusiness && (
                  <TextField
                    id={FIELD_IDS.company_name}
                    label="Company name"
                    required
                    value={values.companyName}
                    onChange={(value) => set('companyName', 'company_name', value)}
                    error={error('company_name')}
                    autoComplete="organization"
                  />
                )}
                <TextField
                  id={FIELD_IDS.name}
                  label={isBusiness ? 'Main contact' : 'Full name'}
                  required
                  value={values.name}
                  onChange={(value) => set('name', 'name', value)}
                  error={error('name')}
                  autoComplete="name"
                />
                {isBusiness && (
                  <TextField
                    id={FIELD_IDS.contact_person}
                    label="Contact person"
                    value={values.contactPerson}
                    onChange={(value) => set('contactPerson', 'contact_person', value)}
                    error={error('contact_person')}
                    placeholder="Optional"
                  />
                )}
                {signsIn ? (
                  <Field className="sm:col-span-2">
                    <FieldLabel htmlFor={FIELD_IDS.email}>Email</FieldLabel>
                    <Input
                      id={FIELD_IDS.email}
                      value={initial.email}
                      readOnly
                      aria-describedby={`${FIELD_IDS.email}-description`}
                    />
                    <FieldDescription id={`${FIELD_IDS.email}-description`}>
                      The customer signs in to Helm with this address, so a new one has to confirm
                      first. Your other changes save as usual.
                    </FieldDescription>
                    <SignInEmailPanel
                      key={signInPanel.key}
                      idPrefix="customer-edit"
                      who="customer"
                      currentEmail={initial.email}
                      pending={signIn?.pending ?? null}
                      startEditing={signInPanel.open}
                      initialNewEmail={signInPanel.newEmail}
                      onPendingChange={() => onSignInChange?.()}
                      actions={{
                        request: async (newEmail) =>
                          (await apiClient.requestCustomerSignInEmailChange(customer.id, newEmail))
                            .pending,
                        resend: async () =>
                          (await apiClient.resendCustomerSignInEmailChange(customer.id)).pending,
                        cancel: async () => {
                          await apiClient.cancelCustomerSignInEmailChange(customer.id)
                        },
                      }}
                    />
                  </Field>
                ) : (
                  /* Not type="email": the browser's own check would block the submit before ours explains it. */
                  <TextField
                    id={FIELD_IDS.email}
                    label="Email"
                    required
                    value={values.email}
                    onChange={(value) => set('email', 'email', value)}
                    error={error('email')}
                    inputMode="email"
                    autoComplete="email"
                  />
                )}
                <TextField
                  id={FIELD_IDS.phone}
                  label="Phone"
                  type="tel"
                  required
                  value={values.phone}
                  onChange={(value) => set('phone', 'phone', value)}
                  error={error('phone')}
                  placeholder="+256 7XX XXX XXX"
                  autoComplete="tel"
                />
                <TextField
                  id={FIELD_IDS.tin_number}
                  label="TIN"
                  required
                  value={values.tinNumber}
                  onChange={(value) => set('tinNumber', 'tin_number', value)}
                  error={error('tin_number')}
                  inputMode="numeric"
                />
              </div>
            </FieldGroup>

            <FieldSet>
              <FieldLegend variant="label">Invoicing</FieldLegend>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField
                  id={FIELD_IDS.default_currency}
                  label="Invoice currency"
                  required
                  value={values.defaultCurrency}
                  onChange={(value) =>
                    set('defaultCurrency', 'default_currency', value.toUpperCase())
                  }
                  error={error('default_currency')}
                  maxLength={3}
                />
                <TextField
                  id={FIELD_IDS.customer_language}
                  label="Language"
                  required
                  value={values.customerLanguage}
                  onChange={(value) => set('customerLanguage', 'customer_language', value)}
                  error={error('customer_language')}
                />
                <TextField
                  id={FIELD_IDS.whatsapp_group_id}
                  label="WhatsApp group ID"
                  value={values.whatsappGroupId}
                  onChange={(value) => set('whatsappGroupId', 'whatsapp_group_id', value)}
                  error={error('whatsapp_group_id')}
                  placeholder="Optional"
                />
              </div>
              <TextAreaField
                id={FIELD_IDS.notes}
                label="Notes"
                value={values.notes}
                onChange={(value) => set('notes', 'notes', value)}
                error={error('notes')}
              />
            </FieldSet>

            <div className="space-y-1 rounded-md border bg-surface-subtle p-3 text-sm text-muted-foreground">
              <p>
                Customer ID:{' '}
                <span className="font-mono text-foreground">
                  {customer.customer_number || 'assigned when first used for an inquiry'}
                </span>
              </p>
              {customer.zoho_contact_id && <p>Linked to a Zoho Books contact.</p>}
              <p>
                Helm assigns the customer ID and warehouse code, so they stay the same when you
                save.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <ContactsEditor
              rows={contacts}
              onChange={setContacts}
              errors={listErrors}
              onClearError={clearListError}
            />
          </TabsContent>

          <TabsContent value="addresses">
            <AddressesEditor
              rows={addresses}
              onChange={setAddresses}
              errors={listErrors}
              onClearError={clearListError}
            />
          </TabsContent>

          <TabsContent value="online" className="flex flex-col gap-[var(--form-section-gap)]">
            <Field>
              <FieldLabel htmlFor={FIELD_IDS.portal_status}>Client portal</FieldLabel>
              <EnumSelect
                value={values.portalStatus}
                onValueChange={(value) =>
                  set('portalStatus', 'portal_status', value as CustomerPortalStatus)
                }
                options={PORTAL_STATUSES.map((value) => ({ value, label: formatLabel(value) }))}
                triggerProps={{ id: FIELD_IDS.portal_status, className: 'sm:max-w-xs' }}
              />
              <FieldDescription>
                Where this customer stands with the client portal.
              </FieldDescription>
            </Field>
            {suspending && (
              <Alert role="alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Suspending locks {who} out of Helm and Tarifix as soon as you save: they are
                  signed out everywhere, and every customer page refuses them until you lift it.
                </AlertDescription>
              </Alert>
            )}
            {lifting && (
              <Alert role="status">
                <AlertDescription>
                  Lifting the suspension lets {who} sign in and see their records again.
                </AlertDescription>
              </Alert>
            )}
            <TextField
              id={FIELD_IDS.website_url}
              label="Website"
              value={values.websiteUrl}
              onChange={(value) => set('websiteUrl', 'website_url', value)}
              error={error('website_url')}
              placeholder="https://example.org"
              inputMode="url"
            />
            <div className="grid gap-4 sm:grid-cols-3">
              {SOCIAL_FIELDS.map((social) => (
                <TextField
                  key={social.key}
                  id={`customer-edit-social-${social.key}`}
                  label={social.label}
                  value={values.socialProfiles[social.key] ?? ''}
                  onChange={(value) =>
                    set('socialProfiles', 'social_profiles', {
                      ...values.socialProfiles,
                      [social.key]: value,
                    })
                  }
                  placeholder={social.placeholder}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <AlertDialog
          open={portalConfirm !== null}
          onOpenChange={(next) => !next && setPortalConfirm(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {suspending
                  ? `Suspend ${customer.company_name || customer.name}?`
                  : 'Lift the suspension?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {suspending
                  ? `This locks ${who} out of Helm and Tarifix immediately and signs them out everywhere. Lifting the suspension later restores access.`
                  : `${who === 'this customer' ? 'The customer' : who} can sign in and see their consignments, requests and parcels again.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <TextAreaField
              id="customer-edit-portal-reason"
              label={
                suspending ? 'Why are you suspending them?' : 'Why are you lifting it? (optional)'
              }
              required={suspending}
              rows={3}
              value={portalConfirm?.reason ?? ''}
              onChange={(value) =>
                setPortalConfirm((current) => current && { ...current, reason: value, error: null })
              }
              error={portalConfirm?.error ?? undefined}
              description="Kept in the customer's audit log with your name."
            />
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button
                type="button"
                variant={suspending ? 'destructive' : 'default'}
                onClick={() => void confirmPortalChange()}
              >
                {suspending ? 'Suspend customer' : 'Lift suspension'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </FormDialogContent>
    </FormDialog>
  )
}
